import { thumbnailCache } from '@/services/thumbnail-cache-service';

/**
 * Fetches document thumbnails with caching support
 *
 * @param {Array} documents - Array of document objects with id and pages
 * @param {Function} fetchFromApi - Function to fetch uncached documents from API
 * @param {string} size - The thumbnail size to fetch ('small' or 'large'), defaults to 'small'
 * @returns {Promise<Array>} - Array of document thumbnails
 */
export const fetchDocumentThumbnails = async (documents, fetchFromApi, size = 'small') => {
  try {
    const uncachedDocumentsMap = new Map();
    const cachedResults = [];

    for (const doc of documents) {
      const { id, pages } = doc;
      const docResults = { documentId: id, pages: {} };
      const uncachedPages = [];

      for (const page of pages) {
        const cachedUrl = thumbnailCache.get(id, page, size);
        if (cachedUrl) {
          docResults.pages[page] = { [size]: cachedUrl };
        } else {
          uncachedPages.push(page);
        }
      }

      if (Object.keys(docResults.pages).length > 0) {
        cachedResults.push(docResults);
      }

      if (uncachedPages.length > 0) {
        const existingDoc = uncachedDocumentsMap.get(id);
        if (existingDoc) {
          const mergedPages = [...new Set([...existingDoc.pages, ...uncachedPages])];
          uncachedDocumentsMap.set(id, { id, pages: mergedPages });
        } else {
          uncachedDocumentsMap.set(id, { id, pages: uncachedPages });
        }
      }
    }

    // Convert map to array
    const uncachedDocuments = Array.from(uncachedDocumentsMap.values());

    // If all documents were in cache, return early
    if (uncachedDocuments.length === 0) {
      return cachedResults;
    }

    // Fetch uncached documents from API
    const apiResults = await fetchFromApi(uncachedDocuments);

    // Cache the results
    for (const doc of apiResults) {
      const { documentId, pages } = doc;
      for (const [pageNum, pageData] of Object.entries(pages)) {
        if (pageData[size]) {
          thumbnailCache.set(documentId, pageNum, pageData[size], size);
        }
      }
    }
    // Create a map to merge by documentId
    const mergedMap = new Map();

    // Add cachedResults to the map
    for (const doc of cachedResults) {
      mergedMap.set(doc.documentId, { ...doc });
    }

    // Merge in apiResults
    for (const doc of apiResults) {
      if (mergedMap.has(doc.documentId)) {
        // Merge pages objects
        mergedMap.set(doc.documentId, {
          ...mergedMap.get(doc.documentId),
          ...doc,
          pages: {
            ...mergedMap.get(doc.documentId).pages,
            ...doc.pages,
          },
        });
      } else {
        mergedMap.set(doc.documentId, { ...doc });
      }
    }

    const result = Array.from(mergedMap.values());
    return result;
  } catch (error) {
    console.error('Failed to load documents thumbs:', error);
    return [];
  }
};
